import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import { PageHeader, PageShell } from "../../../../components/UI/Page";
import { LoadingState } from "../../../../components/UI/States";
import { BackLink } from "../../../../components/WindowDoor/wdUi";
import { selectUser } from "../../../../features/userSlice";
import {
  CONTACT_METHOD_LABELS,
  EMPTY_CHECKLIST,
  STATUS,
  addressLabel,
  customerLabel,
  daysActive,
  formatDate,
} from "../../../../components/WindowDoor/ServiceRequests/constants";

import {
  assignServiceRequest,
  fetchServiceRequest,
  fetchServiceTechnicians,
  saveServiceRequestChecklist,
  updateServiceRequestDetails,
  updateServiceRequestStatus,
} from "../../../../components/WindowDoor/ServiceRequests/api";
import {
  ActivityLogButton,
  AttachmentsCard,
  ChatThread,
  DetailRow,
  StatusChip,
  SummaryCard,
  dashboardPath,
} from "./ServiceRequestShared";
// Field set for the inline editor — mirrors EDITABLE_FIELDS on the server.
// `full` fields span the grid; `options` render a select.
const EDIT_FIELDS = [
  { key: "filled_out_by", label: "Filled Out By" },
  { key: "salesperson_email", label: "Salesperson Email" },
  { key: "sales_number", label: "Sales Number" },
  { key: "manufacturer", label: "Manufacturer" },
  { key: "home_occupied", label: "Home Occupied", options: ["Yes", "No"] },
  { key: "homeowner_name", label: "Homeowners Name" },
  { key: "lot", label: "Lot" },
  { key: "address", label: "Address" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "Zip Code" },
  {
    key: "preferred_contact",
    label: "Preferred Contact",
    options: ["Phone", "Email"],
  },
  { key: "customer_phone", label: "Customer Phone" },
  { key: "customer_email", label: "Customer Email" },
  { key: "contractor", label: "Contractor" },
  { key: "age_of_product", label: "Age of Product" },
  { key: "contractor_office_phone", label: "Contractor Office Phone" },
  { key: "contractor_cell_phone", label: "Contractor Cell Phone" },
  { key: "contractor_email", label: "Contractor Email" },
  { key: "cig_number", label: "CIG #" },
  { key: "product_id", label: "Product ID" },
  { key: "bill_to", label: "Bill To" },
  { key: "location", label: "Location" },
  { key: "account_number", label: "Account Number" },
  { key: "job_number", label: "Job Number" },
  { key: "describe_issue", label: "Describe Issue", full: true, multiline: true },
];

const EDIT_LABELS = EDIT_FIELDS.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});


const CONTACT_METHODS = ["verbal", "voicemail", "not_occupied"];

const Check = ({ checked, onChange, label, disabled, className = "" }) => (
  <label
    className={`flex min-h-[36px] items-center gap-2.5 text-sm font-medium ${
      disabled ? "opacity-50" : "cursor-pointer"
    } ${className}`}
  >
    <input
      type="checkbox"
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 accent-sky-600"
    />
    <span>{label}</span>
  </label>
);

const ServiceRequestDetail = () => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  // Back to the queue as the user left it: filters, page, scroll.
  const backToQueue = () =>
    navigate(dashboardPath(), { state: { restoreScroll: true } });

  const [request, setRequest] = useState(null);
  const [checklist, setChecklist] = useState(EMPTY_CHECKLIST);
  const [technicians, setTechnicians] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [savingDetails, setSavingDetails] = useState(false);
  // Bumped after edits so the chat rerenders the new system log line.
  const [chatVersion, setChatVersion] = useState(0);

  const actor = useMemo(
    () => ({ userID: user?.userID, name: user?.name, email: user?.email }),
    [user?.userID, user?.name, user?.email]
  );

  const load = () => {
    fetchServiceRequest(id)
      .then((data) => {
        setRequest(data);
        setChecklist({ ...EMPTY_CHECKLIST, ...(data?.checklist || {}) });
      })
      .catch((err) =>
        setLoadError(err.message || "Could not load this service request.")
      );
  };

  useEffect(() => {
    load();
    fetchServiceTechnicians()
      .then((data) => setTechnicians(Array.isArray(data) ? data : []))
      .catch(() => setTechnicians([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setCheck = (field) => (value) =>
    setChecklist((prev) => {
      const next = { ...prev, [field]: value };
      // Unticking "Homeowner Contacted" clears how they were contacted, so
      // the two can't disagree.
      if (field === "homeowner_contacted" && !value) next.contact_method = null;
      return next;
    });

  const saveChecklist = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveServiceRequestChecklist(id, checklist, actor);
      setNotice(
        result.changed
          ? `Saved — status is now "${result.status}".`
          : "Checklist saved."
      );
      load();
    } catch (err) {
      setError(err.message || "Could not save the checklist.");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action, label) => {
    setBusyAction(action);
    setError(null);
    setNotice(null);
    try {
      const result = await updateServiceRequestStatus(id, action, actor);
      setNotice(`${label} — status is now "${result.status}".`);
      load();
    } catch (err) {
      setError(err.message || `Could not ${label.toLowerCase()}.`);
    } finally {
      setBusyAction(null);
    }
  };

  const startEditing = () => {
    const values = {};
    EDIT_FIELDS.forEach(({ key }) => {
      values[key] =
        key === "home_occupied"
          ? request.home_occupied
            ? "Yes"
            : "No"
          : request[key] || "";
    });
    setEditValues(values);
    setEditing(true);
    setNotice(null);
    setError(null);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    setError(null);
    try {
      const result = await updateServiceRequestDetails(id, editValues, actor);
      setEditing(false);
      if (result.changes?.length) {
        setNotice(
          `Saved ${result.changes.length} change${
            result.changes.length === 1 ? "" : "s"
          } — logged to the request's chat.`
        );
        setChatVersion((version) => version + 1);
      } else {
        setNotice("No changes to save.");
      }
      load();
    } catch (err) {
      setError(err.message || "Could not save the details.");
    } finally {
      setSavingDetails(false);
    }
  };

  const assign = async (userID) => {
    // Re-selecting an imported name-only assignee is a no-op, not an unassign.
    if (String(userID).startsWith("name:")) return;
    setError(null);
    try {
      const technician = technicians.find(
        (item) => String(item.userID) === String(userID)
      );
      await assignServiceRequest(
        id,
        technician
          ? {
              userID: technician.userID,
              name: technician.name,
              email: technician.email,
            }
          : null,
        actor
      );
      load();
    } catch (err) {
      setError(err.message || "Could not save the assignment.");
    }
  };

  if (loadError) {
    return (
      <PageShell size="sm">
        <BackLink onClick={backToQueue}>
          All requests
        </BackLink>
        <Alert severity="error">{loadError}</Alert>
      </PageShell>
    );
  }

  if (!request) {
    return (
      <PageShell size="2xl">
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading service request…" />
        </div>
      </PageShell>
    );
  }

  const takenOff = request.status === STATUS.TAKEN_OFF;
  const onHold = request.status === STATUS.INACTIVE;
  // Saved state, not the unsaved boxes: the server refuses Take Off once
  // either completion box is checked.
  const serviceCompleted = Boolean(
    request.checklist?.tech_service_completed ||
      request.checklist?.office_service_completed
  );

  return (
    <PageShell size="full">
      <BackLink onClick={backToQueue}>
        All requests
      </BackLink>
      <PageHeader
        icon={HomeRepairServiceOutlinedIcon}
        tone="indigo"
        eyebrow="Service Requests"
        title={`Service Request #${request.entry_number}`}
        description={
          <>
            Submitted {formatDate(request.entry_date)} · open{" "}
            {daysActive(request)} day{daysActive(request) === 1 ? "" : "s"}
            {request.imported && " · imported from the old system"}
          </>
        }
        actions={
          <>
            <ActivityLogButton requestId={request._id} />
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              href={`/api/windowdoor/servicerequests/${request._id}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              Download PDF
            </Button>
          </>
        }
      />

      {/* Summary strip */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Salesperson" value={request.salesperson} />
        <SummaryCard label="Customer" value={customerLabel(request)} />
        <SummaryCard
          label="Contractor"
          value={request.contractor}
          accent="border-l-indigo-500"
        />
        <SummaryCard label="Status" accent="border-l-amber-500">
          <StatusChip status={request.status} />
        </SummaryCard>
      </div>

      {error && (
        <Alert severity="error" className="!mb-4">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" className="!mb-4">
          {notice}
        </Alert>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0 space-y-6">
            {/* Checklist */}
            <div className="app-card rounded-2xl border">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b app-divider px-5 py-3.5">
                <h2 className="font-semibold">Checklist</h2>
                <div className="flex flex-wrap gap-2">
                  {!takenOff && (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={busyAction !== null}
                      onClick={() =>
                        runAction(
                          onHold ? "unhold" : "hold",
                          onHold ? "Taken off hold" : "Put on hold"
                        )
                      }
                    >
                      {onHold ? "Resume" : "Hold"}
                    </Button>
                  )}
                  {(takenOff || !serviceCompleted) && (
                    <Button
                      size="small"
                      variant="contained"
                      color={takenOff ? "success" : "error"}
                      disabled={busyAction !== null}
                      onClick={() =>
                        runAction(
                          takenOff ? "reopen" : "take_off",
                          takenOff ? "Reopened" : "Taken off"
                        )
                      }
                    >
                      {takenOff ? "Reopen" : "Take Off"}
                    </Button>
                  )}
                  {request.assigned_to?.name && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => assign("")}
                    >
                      Remove {request.assigned_to.name}
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-5 px-5 py-4">
                {(takenOff || onHold) && (
                  <Alert severity="info">
                    This request is {takenOff ? "taken off" : "on hold"}, so
                    checklist changes won't move it forward until you{" "}
                    {takenOff ? "reopen" : "resume"} it.
                    {takenOff &&
                      " Checking Tech or Office Service Completed reopens it for you."}
                  </Alert>
                )}

                <div className="app-surface grid gap-4 rounded-xl border p-4 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Check
                      label="Homeowner Contacted"
                      checked={checklist.homeowner_contacted}
                      onChange={setCheck("homeowner_contacted")}
                    />
                    <div className="ml-7 space-y-0.5">
                      {CONTACT_METHODS.map((method) => (
                        <label
                          key={method}
                          className={`flex min-h-[32px] items-center gap-2 text-sm ${
                            checklist.homeowner_contacted
                              ? "cursor-pointer"
                              : "opacity-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="contact_method"
                            disabled={!checklist.homeowner_contacted}
                            checked={checklist.contact_method === method}
                            onChange={() =>
                              setChecklist((prev) => ({
                                ...prev,
                                contact_method: method,
                              }))
                            }
                            className="h-4 w-4 accent-sky-600"
                          />
                          <span>{CONTACT_METHOD_LABELS[method]}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Check
                    label="Job Quoted"
                    checked={checklist.job_quoted}
                    onChange={setCheck("job_quoted")}
                    className="items-start"
                  />
                  <Check
                    label="Parts Ordered"
                    checked={checklist.parts_ordered}
                    onChange={setCheck("parts_ordered")}
                    className="items-start"
                  />
                </div>

                <div className="app-surface grid gap-4 rounded-xl border p-4 sm:grid-cols-2">
                  <Check
                    label="Tech Service Completed"
                    checked={checklist.tech_service_completed}
                    onChange={setCheck("tech_service_completed")}
                  />
                  <Check
                    label="Office Service Completed"
                    checked={checklist.office_service_completed}
                    onChange={setCheck("office_service_completed")}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold">Order Number</p>
                    <TextField
                      fullWidth
                      size="small"
                      inputProps={{ "aria-label": "Order Number" }}
                      value={checklist.order_number || ""}
                      onChange={(event) =>
                        setChecklist((prev) => ({
                          ...prev,
                          order_number: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold">Assigned Technician</p>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      inputProps={{ "aria-label": "Assigned Technician" }}
                      value={
                        request.assigned_to?.userID ||
                        (request.assigned_to?.name
                          ? `name:${request.assigned_to.name}`
                          : "")
                      }
                      onChange={(event) => assign(event.target.value)}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {/* Imported, name-only assignee (predates their Big C
                          Node account); picking a real account replaces it. */}
                      {request.assigned_to?.name &&
                        !request.assigned_to?.userID && (
                          <MenuItem value={`name:${request.assigned_to.name}`}>
                            {request.assigned_to.name}
                          </MenuItem>
                        )}
                      {technicians.map((technician) => (
                        <MenuItem
                          key={technician.userID}
                          value={technician.userID}
                        >
                          {technician.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t app-divider pt-4">
                  <Button
                    variant="contained"
                    onClick={saveChecklist}
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {saving ? "Saving…" : "Save Checklist"}
                  </Button>
                  <p className="app-muted text-xs">
                    Status follows the furthest box ticked.
                  </p>
                </div>
              </div>
            </div>

            {/* Request details — read view, inline editor, and change log */}
            <div className="app-card rounded-2xl border">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b app-divider px-5 py-3.5">
                <h2 className="font-semibold">Request Details</h2>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant={showLog ? "contained" : "outlined"}
                    color="inherit"
                    startIcon={<HistoryIcon fontSize="small" />}
                    onClick={() => setShowLog((open) => !open)}
                  >
                    Log ({request.edit_history?.length || 0})
                  </Button>
                  {editing ? (
                    <>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={savingDetails}
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={savingDetails}
                        onClick={saveDetails}
                        startIcon={
                          savingDetails ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null
                        }
                      >
                        {savingDetails ? "Saving…" : "Save Changes"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={startEditing}
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              {editing ? (
                <div className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
                  {EDIT_FIELDS.map(({ key, label, options, full, multiline }) => (
                    <div
                      key={key}
                      className={full ? "sm:col-span-2 lg:col-span-3" : ""}
                    >
                      <p className="app-muted mb-1 text-xs font-medium">
                        {label}
                      </p>
                      {options ? (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          value={editValues[key] ?? ""}
                          onChange={(event) =>
                            setEditValues((prev) => ({
                              ...prev,
                              [key]: event.target.value,
                            }))
                          }
                        >
                          {options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          multiline={Boolean(multiline)}
                          minRows={multiline ? 3 : undefined}
                          value={editValues[key] ?? ""}
                          onChange={(event) =>
                            setEditValues((prev) => ({
                              ...prev,
                              [key]: event.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                  ))}
                  <p className="app-muted text-xs sm:col-span-2 lg:col-span-3">
                    Every change is logged with your name in the change log and
                    the request's chat.
                  </p>
                </div>
              ) : (
                <div className="grid gap-x-6 gap-y-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
                  <DetailRow label="Filled Out By" value={request.filled_out_by} />
                  <DetailRow
                    label="Salesperson Email"
                    value={request.salesperson_email}
                  />
                  <DetailRow label="Sales Number" value={request.sales_number} />
                  <DetailRow label="Manufacturer" value={request.manufacturer} />
                  <DetailRow
                    label="Home Occupied"
                    value={request.home_occupied ? "Yes" : "No"}
                  />
                  <DetailRow label="Homeowner" value={customerLabel(request)} />
                  <DetailRow
                    label="Address"
                    value={addressLabel(request)}
                    className="sm:col-span-2"
                  />
                  <DetailRow label="Zip" value={request.zip} />
                  <DetailRow
                    label="Preferred Contact"
                    value={request.preferred_contact}
                  />
                  <DetailRow label="Customer Phone" value={request.customer_phone} />
                  <DetailRow label="Customer Email" value={request.customer_email} />
                  <DetailRow label="Contractor" value={request.contractor} />
                  <DetailRow
                    label="Contractor Office"
                    value={request.contractor_office_phone}
                  />
                  <DetailRow
                    label="Contractor Cell"
                    value={request.contractor_cell_phone}
                  />
                  <DetailRow
                    label="Contractor Email"
                    value={request.contractor_email}
                  />
                  <DetailRow label="Age of Product" value={request.age_of_product} />
                  <DetailRow label="CIG #" value={request.cig_number} />
                  <DetailRow label="Product ID" value={request.product_id} />
                  <DetailRow label="Bill To" value={request.bill_to} />
                  <DetailRow label="Location" value={request.location} />
                  <DetailRow label="Account #" value={request.account_number} />
                  <DetailRow label="Job #" value={request.job_number} />
                  <div className="sm:col-span-2 lg:col-span-3">
                    <p className="app-muted text-xs font-medium">
                      Describe Issue
                    </p>
                    <p className="app-surface mt-1.5 whitespace-pre-wrap rounded-xl border px-4 py-3 text-sm leading-relaxed">
                      {request.describe_issue || "—"}
                    </p>
                  </div>
                </div>
              )}

              {showLog && (
                <div className="border-t app-divider px-5 py-4">
                  <p className="app-eyebrow mb-3 flex items-center gap-1.5">
                    <HistoryIcon sx={{ fontSize: 14 }} /> Change log
                  </p>
                  {!request.edit_history?.length && (
                    <p className="app-muted text-sm">
                      No edits yet — changes made through the Edit button will
                      be listed here.
                    </p>
                  )}
                  <ul className="space-y-3">
                    {[...(request.edit_history || [])].reverse().map((entry, index) => (
                      <li key={index} className="text-sm">
                        <p className="app-muted text-xs">
                          {formatDate(entry.date)}
                          {entry.by?.name && ` — ${entry.by.name}`}
                        </p>
                        <ul className="mt-0.5 space-y-0.5">
                          {(entry.changes || []).map((change, changeIndex) => (
                            <li key={changeIndex}>
                              <span className="font-medium">
                                {EDIT_LABELS[change.field] || change.field}:
                              </span>{" "}
                              <span className="app-muted line-through">
                                {change.from || "—"}
                              </span>{" "}
                              → <span>{change.to || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <AttachmentsCard
              request={request}
              user={user}
              onUploaded={() => {
                load();
                setChatVersion((version) => version + 1);
              }}
            />
        </div>

          <ChatThread
            key={chatVersion}
            requestId={request._id}
            user={user}
            title={`Chat #${request.entry_number}`}
          />
      </div>
    </PageShell>
  );
};

export default ServiceRequestDetail;
